import React, { FC, ReactEventHandler, useEffect, useState } from 'react';
import Badge from '../badge/badge';
import OutsideClick from '../outside-click/outside-click';
import Tab, { FlatTab, TabList } from '../tab/tab';

import { Category } from "../../models/Category";

import plusIcon from './plus_darker.svg';
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";

type Props = {
    tabs: any[];
    add: boolean;
    selected: number;
    onSelect: (selected: number) => void;
    className?: string;
    onAdd: ReactEventHandler;
    isFlat?: boolean,
    tabStyle?: {},
    onEdit?: (index) => void,
    forceRefresh?: boolean,
    setForceRefresh?: (forceRefresh: boolean) => void,
    setSelectedTab?: (index: number) => void,
    itemProfile?: any,

    /**
     * In some cases, mainly for sub-tabs, we don't want to show the border because the main tab already has a border.
     */
    showBorder?: boolean
}

export const getTabsListFromLabels = (categories: Category[]): { badge: number; label: string }[] => {
    return categories.map((c: Category) => {
        return {
            label: c.displayName,
            badge: c.count || 0,
            isActive: true
        }
    });
}

const Tabs: FC<Props> = ({ tabs, selected, onSelect, children, className, add, onAdd, isFlat: isFlatParameter = false, tabStyle = {}, onEdit = undefined, forceRefresh = false, setForceRefresh = () => { }, setSelectedTab, itemProfile, showBorder = true }) => {

    const windowWidth = window.innerWidth;
    const [visibleTabCount] = useState(Math.round(((windowWidth / 3) * 2) / 174));
    const activeTabs = tabs.filter(tab => tab.isActive);

    const [shownTabs, setShownTabs] = useState(activeTabs.slice(0, visibleTabCount).map((tab, index) => { return { ...tab, tabIndex: index, isActive: tab.isActive ?? false } }));
    const [hiddenTabs, setHiddenTabs] = useState(activeTabs.slice(visibleTabCount).map((tab, index) => { return { ...tab, tabIndex: index + visibleTabCount, isActive: tab.isActive ?? false } }));

    const [showTabList, setShowTabList] = useState(false)
    const [onHover, setOnHover] = useState(-1);
    const { translate } = useLocale();
    const style = { minWidth: 139 };
    const isFlat = isFlatParameter;

    const buildShownTab: (value: any, index: number, array: any[]) => JSX.Element | null = ({ label, badge, tabIndex, isActive }) => {
        if (isActive) {
            if (isFlat) {
                return <FlatTab key={`tab-${tabIndex}`} onClick={() => onSelect(tabIndex)} label={label} badge={badge} selected={selected === tabIndex} />
            }
            else {
                return <Tab key={`tab-${tabIndex}`}
                    onClick={() => onSelect(tabIndex)}
                    label={label}
                    badge={badge}
                    selected={selected === tabIndex}
                    onEdit={onEdit && (tabIndex > 0) ? () => onEdit(tabIndex) : undefined} />
            }
        }
        else {
            return null;
        }
    };

    const buildHiddenTab: (value: any, index: number, array: any[]) => JSX.Element = ({ label, badge, id }, index) => {
        return <div
            className={`flex flex-row justify-center px-2 py-2 text-sm text-center w-full ${onHover === index ? 'bg-portal-blue-hover bg-opacity-50' : ''} ${hiddenTabs.length - 1 === index ? 'rounded-b-md' : ''}`}
            onMouseEnter={() => setOnHover(index)}
            onMouseLeave={() => setOnHover(-1)}
            onClick={e => replaceSwapTab(index)}>
            <p key={`hidden-${index}`}>
                {label}
                {badge
                    ? <Badge className="ml-2">{badge}</Badge>
                    : null}
            </p>
        </div>
    };

    const resetTabs = (tabCount = visibleTabCount) => {

        setShownTabs(
            activeTabs.slice(0, visibleTabCount).map((tab, index) => {
                return {
                    ...tab,
                    tabIndex: tabs.indexOf(tab),
                    isActive: tab.isActive ?? false,
                };
            })
        );

        setHiddenTabs(
            activeTabs.slice(visibleTabCount).map((tab, index) => {
                return {
                    ...tab,
                    tabIndex: tabs.indexOf(tab),
                    isActive: tab.isActive ?? false,
                };
            })
        );

        if (setSelectedTab && itemProfile && !!!itemProfile.id) {
            setSelectedTab(0);
        }
    }

    const replaceSwapTab = (hiddenTabIndex) => {
        const newShownTabs = shownTabs.concat(hiddenTabs[hiddenTabIndex]);
        const swapTab = newShownTabs.splice(visibleTabCount - 1, 1);
        const newHiddenTabs = hiddenTabs.concat(swapTab);
        newHiddenTabs.splice(hiddenTabIndex, 1);

        setShownTabs([...newShownTabs]);
        setHiddenTabs(newHiddenTabs);
    }

    // FIXME: This is a hack to force the tabs to refresh when the tabs change.
    useEffect(() => {
        resetTabs();
    }, [tabs, forceRefresh === false])

    return <div className={`${className} bg-white overflow-hidden ${showBorder ? ` border-gray-bar border-solid border-b` : ''} ${isFlat ? `` : ` flex flex-col flex-grow`}`}>
        <div className={`flex flex-row ${isFlat ? `justify-start ` : `bg-gray-bar px-2 pt-2 justify-start overflow-x-auto`}`}
            style={{ minHeight: '40px' }}>
            {activeTabs.length > 0 ? shownTabs.map(buildShownTab) : null}
            {activeTabs.length > visibleTabCount && hiddenTabs.length > 0
                ? <OutsideClick onClick={() => setShowTabList(false)}>
                    <TabList key={`tab-swapTab`} onClick={() => setShowTabList(!showTabList)} label={translate.byKey(TranslationKeys.more)} badge={hiddenTabs.length} selected={showTabList}>
                        {showTabList
                            ?
                            <div className="z-10 flex flex-col absolute bg-white items-baseline mx-1 cursor-pointer shadow-xs rounded-b-md" style={style}>
                                {hiddenTabs.map(buildHiddenTab)}
                            </div>
                            : null}
                    </TabList>
                </OutsideClick>
                : null
            }
            {add ?
                <img src={plusIcon}
                    onClick={onAdd}
                    title="Add"
                    className="cursor-pointer pl-2 pr-3 pb-1 pt-1 mb-1" alt="" />
                : null
            }
        </div>
        <div className={`flex flex-col flex-shrink bg-white justify-between overflow-y-auto h-full  ${isFlat ? '' : 'pt-3 pb-6 px-8'}`}
            style={tabStyle ? tabStyle : { height: '100%' }}>
            {children}
        </div>
    </div>;
}

export default Tabs;

